<template>
  <div class="staff-info-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <el-button @click="$router.push('/dashboard')" type="primary" plain style="margin-right: 10px;">
            返回首页
          </el-button>
          <span>服务人员信息</span>
          <div class="search-box">
            <el-input
              v-model="searchName"
              placeholder="请输入姓名搜索"
              style="width: 200px; margin-right: 10px;"
              @keyup.enter="handleSearch"
            />
            <el-button type="primary" @click="handleSearch">搜索</el-button>
            <el-button @click="resetSearch">重置</el-button>
          </div>
        </div>
      </template>
      
      <el-table :data="staffList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="姓名" width="120" />
        <el-table-column prop="gender" label="性别" width="80">
          <template #default="scope">
            {{ scope.row.gender === 1 ? '男' : '女' }}
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" width="120" />
        <el-table-column prop="id_card" label="身份证号" width="180" />
        <el-table-column prop="address" label="地址" />
        <el-table-column prop="service_types" label="服务类型" width="120">
          <template #default="scope">
            {{ formatServiceTypes(scope.row.service_types) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="Number(scope.row.status) === 1 ? 'success' : 'danger'">
              {{ Number(scope.row.status) === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button 
              :type="Number(scope.row.status) === 1 ? 'danger' : 'success'" 
              size="small" 
              @click="handleStatusChange(scope.row)"
            >
              {{ Number(scope.row.status) === 1 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加服务人员' : '编辑服务人员'" 
      v-model="dialogVisible"
      width="500px"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="姓名" prop="name">
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-radio-group v-model="form.gender">
            <el-radio :label="1">男</el-radio>
            <el-radio :label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="form.phone" />
        </el-form-item>
        <el-form-item label="身份证号" prop="id_card">
          <el-input v-model="form.id_card" />
        </el-form-item>
        <el-form-item label="地址" prop="address">
          <el-input v-model="form.address" type="textarea" />
        </el-form-item>
        <el-form-item label="服务类型" prop="service_types">
          <el-select v-model="form.service_types" multiple placeholder="请选择服务类型">
            <el-option label="生活照料" value="life_care" />
            <el-option label="膳食服务" value="meal_service" />
            <el-option label="洗涤服务" value="laundry_service" />
            <el-option label="活动服务" value="activity_service" />
            <el-option label="个性服务" value="custom_service" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'StaffInfo',
  setup() {
    const loading = ref(false)
    const staffList = ref([])
    const dialogVisible = ref(false)
    const dialogType = ref('edit')
    const formRef = ref(null)
    const searchName = ref('')
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    
    const form = ref({
      name: '',
      gender: 1,
      phone: '',
      id_card: '',
      address: '',
      service_types: []
    })
    
    const rules = {
      name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
      gender: [{ required: true, message: '请选择性别', trigger: 'change' }],
      phone: [{ required: true, message: '请输入手机号', trigger: 'blur' }],
      id_card: [{ required: true, message: '请输入身份证号', trigger: 'blur' }],
      address: [{ required: true, message: '请输入地址', trigger: 'blur' }],
      service_types: [{ required: true, message: '请选择服务类型', trigger: 'change' }]
    }
    
    const fetchStaffList = async () => {
      loading.value = true
      try {
        const url = `/api/staff_info.php?page=${currentPage.value}&pageSize=${pageSize.value}${searchName.value ? `&name=${encodeURIComponent(searchName.value)}` : ''}`
        const response = await fetch(url)
        const data = await response.json()
        if (data.code === 0) {
          console.log('Staff list data:', data.data.list)
          staffList.value = data.data.list
          total.value = data.data.total
        } else {
          ElMessage.error(data.msg || '获取服务人员列表失败')
        }
      } catch (error) {
        console.error('获取服务人员列表失败:', error)
        ElMessage.error('获取服务人员列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const formatServiceTypes = (types) => {
      if (!types) return ''
      const typeMap = {
        life_care: '生活照料',
        meal_service: '膳食服务',
        laundry_service: '洗涤服务',
        activity_service: '活动服务',
        custom_service: '个性服务'
      }
      return types.split(',').map(type => typeMap[type] || type).join('、')
    }
    
    const handleEdit = (row) => {
      form.value = {
        ...row,
        service_types: row.service_types ? row.service_types.split(',') : []
      }
      dialogVisible.value = true
    }
    
    const handleStatusChange = async (row) => {
      try {
        const action = Number(row.status) === 1 ? '禁用' : '启用'
        await ElMessageBox.confirm(`确定要${action}该服务人员吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/update_staff_status.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            status: Number(row.status) === 1 ? 0 : 1
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(`${action}成功`)
          fetchStaffList()
        } else {
          ElMessage.error(data.msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新状态失败:', error)
          ElMessage.error('更新状态失败')
        }
      }
    }
    
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        const url = dialogType.value === 'add' ? '/api/add_staff.php' : '/api/update_staff.php'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...form.value,
            service_types: form.value.service_types.join(',')
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(dialogType.value === 'add' ? '添加成功' : '更新成功')
          dialogVisible.value = false
          fetchStaffList()
        } else {
          ElMessage.error(data.message || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
        }
      } catch (error) {
        console.error('提交失败:', error)
        ElMessage.error('提交失败')
      }
    }
    
    const handleSearch = () => {
      currentPage.value = 1
      fetchStaffList()
    }
    
    const resetSearch = () => {
      searchName.value = ''
      currentPage.value = 1
      fetchStaffList()
    }
    
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchStaffList()
    }
    
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchStaffList()
    }
    
    onMounted(() => {
      fetchStaffList()
    })
    
    return {
      loading,
      staffList,
      dialogVisible,
      dialogType,
      formRef,
      form,
      rules,
      searchName,
      currentPage,
      pageSize,
      total,
      fetchStaffList,
      formatServiceTypes,
      handleEdit,
      handleStatusChange,
      handleSubmit,
      handleSearch,
      resetSearch,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.staff-info-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-box {
  display: flex;
  align-items: center;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style> 